import React, { useCallback, useRef, useState } from 'react';
import { getTranslation, useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useGlobalSearchParams, useRouter } from 'expo-router';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import DefaultRouteNavigationHeaderLeft from '@/components/Navigation/Routes/DefaultRouteNavigationHeaderLeft';
import { useDeckStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';

const catalogTitle = getTranslation('nav.routes.deckRecipes.catalogTitle') || 'Deck recipes';

/** Sidebar toggle (via default left) + explicit back — Drawer screens do not get a stack back chevron on web. */
const DeckRecipesNavigationHeaderLeft = React.memo(() => {
  const router = useRouter();
  const { t } = useTranslation();

  const onBack = useCallback(() => {
    if (router.canGoBack?.()) {
      router.back();
      return;
    }
    router.push('/deck');
  }, [router]);

  return (
    <DefaultRouteNavigationHeaderLeft>
      <NavigationBarButton
        icon="chevron-left"
        tooltip={t('nav.routes.deckRecipes.backToDeckTitle')}
        accessibilityLabel={t('nav.routes.deckRecipes.backToDeck')}
        onPress={onBack}
      />
    </DefaultRouteNavigationHeaderLeft>
  );
});

DeckRecipesNavigationHeaderLeft.displayName = 'DeckRecipesNavigationHeaderLeft';

/** /decks/recipes/[slug] and /decks/recipes/category/[slug] — sidebar + back to full catalog (or stack pop). */
const DeckRecipesSlugNavigationHeaderLeft = React.memo(() => {
  const router = useRouter();
  const { t } = useTranslation();

  const onBack = useCallback(() => {
    if (router.canGoBack?.()) {
      router.back();
      return;
    }
    router.push('/decks/recipes');
  }, [router]);

  return (
    <DefaultRouteNavigationHeaderLeft>
      <NavigationBarButton
        icon="chevron-left"
        tooltip={t('nav.routes.deckRecipes.backToRecipesTitle')}
        accessibilityLabel={t('nav.routes.deckRecipes.backToRecipes')}
        onPress={onBack}
      />
    </DefaultRouteNavigationHeaderLeft>
  );
});

DeckRecipesSlugNavigationHeaderLeft.displayName = 'DeckRecipesSlugNavigationHeaderLeft';

function deckRecipeSlugFromParams(params) {
  const raw = params?.slug;
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw) && raw[0]) return String(raw[0]);
  return '';
}

/** /decks/recipes/[slug] — mirrors page `handleUseRecipe` (clone template → /deck). */
const DeckRecipesSlugNavigationHeaderRight = React.memo(() => {
  const router = useRouter();
  // Drawer headers inherit the layout's local params, which omit the recipe
  // slug. Read the active route so the header CTA follows recipe navigation.
  const params = useGlobalSearchParams();
  const slug = deckRecipeSlugFromParams(params);
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const { createDeck } = useDeckStore((s) => s.actions);
  const requireAuth = useRequireAuth();
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const onUseRecipe = useCallback(async () => {
    if (!slug || busyRef.current) return;
    const ran = requireAuth(() => true, {
      source: 'deck_recipe_detail_header_use_recipe',
      title: 'Sign in to use this recipe',
      subtitle: 'Recipes clone into a deck on your account so you can customize and save your workspace.',
      icon: 'layer-group',
    });
    if (ran !== true) return;
    busyRef.current = true;
    setBusy(true);
    try {
      // Creation resolves the recipe and its name on the server. A separate
      // lookup can go stale and used to fail without feedback here.
      const deckSlug = await createDeck({ templateSlug: slug });
      if (deckSlug) {
        router.push({ pathname: '/deck', params: { d: deckSlug } });
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [slug, requireAuth, createDeck, router]);

  const useLabel = t('nav.routes.deckRecipes.useThisRecipe');
  const creatingLabel = t('nav.routes.deckRecipes.creatingDeck');

  return (
    <NavigationBarButtonContainer rightPadding>
      <NavigationBarButton
        icon={busy ? 'spinner' : 'plus'}
        solidIcon={busy}
        tooltip={t('nav.routes.deckRecipes.useThisRecipeTitle')}
        label={isXS ? undefined : (busy ? creatingLabel : useLabel)}
        onPress={onUseRecipe}
        disabled={!slug || busy}
      />
    </NavigationBarButtonContainer>
  );
});

DeckRecipesSlugNavigationHeaderRight.displayName = 'DeckRecipesSlugNavigationHeaderRight';

const DeckRecipesNavigationHeaderRight = React.memo(() => {
  const { isXS } = useResponsiveness();
  const router = useRouter();

  const onDeck = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    router.push('/deck');
  }, [router]);

  const deckLabel = getTranslation('nav.routes.deckRecipes.openDeck') || 'Deck';

  return (
    <NavigationBarButtonContainer rightPadding>
      <NavigationBarButton
        icon="table-columns"
        tooltip={getTranslation('nav.routes.deckRecipes.openDeckTitle') || 'Go to your deck workspace'}
        label={isXS ? undefined : deckLabel}
        onPress={onDeck}
      />
    </NavigationBarButtonContainer>
  );
});

DeckRecipesNavigationHeaderRight.displayName = 'DeckRecipesNavigationHeaderRight';

/** /decks/recipes — catalog index (SEO + discovery). */
const DeckRecipesNavigationOptions = {
  title: catalogTitle,
  headerTitle: catalogTitle,
  headerBackVisible: false,
  headerShown: true,
  headerLeft: () => <DeckRecipesNavigationHeaderLeft />,
  headerRight: () => <DeckRecipesNavigationHeaderRight />,
};

/**
 * /decks/recipes/[slug] — recipe guide + clone CTA.
 * @param {string} [recipeName] — hydrated template name when available
 */
export function getDeckRecipesSlugRouteNavigationOptions(recipeName) {
  const fb = getTranslation('nav.routes.deckRecipes.recipeFallback') || 'Deck recipe';
  const t = (recipeName && String(recipeName).trim()) || fb;
  return {
    title: t,
    headerTitle: t,
    headerBackVisible: false,
    headerShown: true,
    headerLeft: () => <DeckRecipesSlugNavigationHeaderLeft />,
    headerRight: () => <DeckRecipesSlugNavigationHeaderRight />,
  };
}

/**
 * /decks/recipes/category/[slug]
 * @param {string} [categoryLabel] — human-readable category name when available
 */
export function getDeckRecipesCategoryRouteNavigationOptions(categoryLabel) {
  const fb = getTranslation('nav.routes.deckRecipes.categoryFallback') || 'Recipes by category';
  const t = (categoryLabel && String(categoryLabel).trim()) || fb;
  return {
    title: t,
    headerTitle: t,
    headerBackVisible: false,
    headerShown: true,
    headerLeft: () => <DeckRecipesSlugNavigationHeaderLeft />,
    headerRight: () => <DeckRecipesNavigationHeaderRight />,
  };
}

export default DeckRecipesNavigationOptions;
